// src/components/Login/ForgotPasswordForm.tsx
import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { requestPasswordReset } from '../../api/userService'
import logo from '../../assets/damelio_logo_blanco.png'
import logo2 from '../../assets/damelio_texto_blanco.png'

interface ForgotPasswordFormProps {
  onBackToLogin: () => void
}

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({
  onBackToLogin,
}) => {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [isButtonDisabled, setIsButtonDisabled] = useState(false)
  const [countdown, setCountdown] = useState(60)
  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setMessage('')
    setError('')

    try {
      const response = await requestPasswordReset(email)
      if (response.error) throw new Error(response.error)
      setMessage(t('forgotPassword.successMessage'))
      setIsButtonDisabled(true)
      setCountdown(60)
    } catch (err) {
      setError(t('forgotPassword.errorMessage'))
    }
  }

  useEffect(() => {
    let interval: NodeJS.Timeout
    if (isButtonDisabled) {
      interval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(interval)
            setIsButtonDisabled(false)
            return 60
          }
          return prev - 1
        })
      }, 1000)
    }
    return () => clearInterval(interval)
  }, [isButtonDisabled])

  return (
    <div className="w-full max-w-sm mx-auto space-y-6 text-white">
      <div
        className="text-center cursor-pointer transition-transform duration-300"
        onClick={() => navigate('/')}
      >
        <img src={logo} alt={t('login.altLogo')} className="mx-auto h-28" />
        <img
          src={logo2}
          alt={t('login.altLogo2')}
          className="mx-auto h-16 mt-2"
        />
      </div>

      <h2 className="text-2xl font-bold text-center">{t('forgotPassword.title')}</h2>
      <p className="text-center text-gray-300">{t('forgotPassword.intro')}</p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium text-gray-200"
          >
            {t('forgotPassword.labels.email')}
          </label>
          <div className="mt-1">
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('forgotPassword.placeholders.email')}
              required
              className="
                block w-full px-4 py-2
                bg-gray-800 border border-gray-700
                rounded-lg text-white placeholder-gray-500
                focus:outline-none focus:ring-2 focus:ring-blue-500
                focus:border-blue-500
                transition
              "
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={isButtonDisabled}
          className={`
            w-full flex items-center justify-center gap-2
            text-white font-semibold rounded-lg transition
            disabled:opacity-50 disabled:cursor-not-allowed
            ${isButtonDisabled
              ? 'bg-gray-600'
              : 'bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700'}
            px-4 py-2
          `}
        >
          {isButtonDisabled
            ? `${t('forgotPassword.buttons.resendIn')} ${countdown}s`
            : t('forgotPassword.buttons.sendEmail')}
        </button>
      </form>

      {message && (
        <p className="text-green-400 text-center">{message}</p>
      )}
      {error && <p className="text-red-500 text-center">{error}</p>}

      <div className="mt-4 text-center">
        <button
          onClick={onBackToLogin}
          className="text-blue-400 hover:text-blue-300 text-sm transition"
        >
          {t('forgotPassword.buttons.backToLogin')}
        </button>
      </div>
    </div>
  )
}

export default ForgotPasswordForm
